import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";

export function requireLeagueOrgId(orgId) {
  const value = String(orgId || "").trim();
  if (!value) throw new Error("Matrix League requires an orgId.");
  return value;
}

export function leagueDataRef(orgId) {
  return doc(db, "orgs", requireLeagueOrgId(orgId), "league", "data");
}

export function leagueSettingsRef(orgId) {
  return doc(db, "orgs", requireLeagueOrgId(orgId), "league", "settings");
}

export function leagueCollection(orgId, name) {
  return collection(leagueDataRef(orgId), String(name));
}

export function leagueDocument(orgId, name, id) {
  return doc(leagueCollection(orgId, name), String(id));
}

export function createLeagueBatch() {
  return writeBatch(db);
}

export const leagueCollections = Object.freeze({
  seasons: "seasons",
  clubs: "clubs",
  fixtures: "fixtures",
  standings: "standings",
  results: "results",
  awards: "awards",
  competitions: "competitions",
  history: "history",
});

export async function ensureLeagueDataBoundary(orgId) {
  await setDoc(leagueDataRef(orgId), {
    schemaVersion: 3,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

const LEGACY_COLLECTIONS = {
  seasons: "leagueSeasons",
  clubs: "leagueClubs",
  fixtures: "leagueFixtures",
};

export async function migrateLegacyLeagueData(orgId) {
  const safeOrgId = requireLeagueOrgId(orgId);
  await ensureLeagueDataBoundary(safeOrgId);
  const summary = {};

  for (const [targetName, legacyName] of Object.entries(LEGACY_COLLECTIONS)) {
    const legacySnapshot = await getDocs(collection(db, "orgs", safeOrgId, legacyName));
    if (legacySnapshot.empty) {
      summary[targetName] = 0;
      continue;
    }
    const batch = writeBatch(db);
    legacySnapshot.docs.forEach((legacyDoc) => {
      batch.set(leagueDocument(safeOrgId, targetName, legacyDoc.id), {
        ...legacyDoc.data(),
        migratedFrom: legacyName,
        migratedAt: serverTimestamp(),
      }, { merge: true });
    });
    await batch.commit();
    summary[targetName] = legacySnapshot.size;
  }

  await setDoc(leagueDataRef(safeOrgId), {
    schemaVersion: 3,
    legacyMigrationCompletedAt: serverTimestamp(),
    legacyMigrationSummary: summary,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return summary;
}
